import React, {useContext, useEffect, useState} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {LinearProgress} from "@material-ui/core";
import {checkSearchReferenceStatus} from '../Foundations/API/SearchApi';
import appContext from '../Foundations/Contexts/AppContext';
import {ISearchResponse} from '../Foundations/Types/ISearchResponse';
import SearchState from '../Foundations/Enum/SearchState';
import Button from '../Components/Button';
import SvgIcon from '../Components/Icons/SvgIcon';
import BlueSquiggle from '../Components/Icons/BlueSquiggle';
import CopyContentIcon from '../Components/Icons/CopyContentIcon';
import searchContext from '../Foundations/Contexts/SearchContext';

interface IRouteProps {
    referenceId: string;
}

interface IProps extends RouteComponentProps<IRouteProps> {}

const SearchRoute: React.FC<IProps> = ({ match, history }) => {
    const { env } = useContext(appContext);
    const { setFinishedQueue } = useContext(searchContext);
    const origin: string = window.location.origin;
    const [time, setTime] = useState<number>(0);
    const [timeLeft, setTimeLeft] = useState<string>('1 min');
    const [copySuccess, setCopySuccess] = useState('');

    const copyToClipboard = (textToCopy: string) => {
        navigator.clipboard.writeText(textToCopy);
        setCopySuccess('Copied to clipboard!');
        setTimeout(() => setCopySuccess(''), 1500);
    }

    useEffect(() => {
        let interval = setInterval(() => {
            setTime(time + 2000);
            let timeLeftText = '1 min';
            if (time >= 30000) {
                timeLeftText = '30 sec';
            } else if (time >= 15000) {
                timeLeftText = '45 sec';
            }
            setTimeLeft(timeLeftText);
        }, 2000);

        return () => {
            clearInterval(interval);
        }
    });

    useEffect(() => {
        const refRequest = (waitTime: number): number => {
            return window.setTimeout(() => {
                checkSearchReferenceStatus(env, match.params.referenceId)
                    .then((response: ISearchResponse) => {
                        if (response.state === SearchState.STATE_CAR_EXPORTED) {
                            setFinishedQueue({
                                isFinished: true,
                                isStarted: true,
                                parameters: response.parameters,
                                ref: match.params.referenceId,
                                searchState: response.state
                            })
                        } else {
                            refRequest(5000);
                        }
                    })
                    .catch((err) => console.error(err));
            }, waitTime)
        }
        const reqInterval: number = refRequest(0);
        return () => clearTimeout(reqInterval);
    }, [env, history, match.params.referenceId, setFinishedQueue]);

    return (
        <div className="bg-gray-100 text-sm font-main">
            <div className="container flex mx-auto h-[70vh] items-center">
                <div className="w-3/6 relative z-30">
                    <h1 className="font-bold text-black-400 text-2xl mb-4">
                        { time >= 0 && time <= 2000 && 'Loading depots…'}
                        { time >= 4000 && time <= 6000 && 'Processing rates & availability…'}
                        { time > 6000 && 'Preparing data…'}
                    </h1>
                    <LinearProgress value={time === 0 ? 10 : time < 60000 ? (10 + (time / 600)) : 100} variant='determinate' className="py-1 w-4/6 rounded-xl" classes={{ barColorPrimary: '!bg-blue', colorPrimary: '!bg-gray-300' }}/>
                    <p className="text-gray-700 text-base my-3">{timeLeft} remaining</p>
                    <p className="text-gray-700 w-4/6 mt-9 my-4 leading-5">
                        Have other things to do? Use the link below to come back to your ratency results any time.
                    </p>
                   <Button
                        text=''
                        onClick={ () => copyToClipboard(`${origin}/app/search/${match.params.referenceId}`) }
                        textStyle="text-blue font-bold break-all py-2 pl-3 pr-10"
                        justify="justify-start"
                        width="w-auto max-w-fit relative"
                        height="h-full"
                        bg="bg-white border-gray-400 border"
                   >
                        {origin}/app/search/{match.params.referenceId} 
                        <SvgIcon className="absolute right-2 z-0 fill-blue" width="24" height="24" viewBox="0 0 24 24">
                            <CopyContentIcon />
                        </SvgIcon>
                   </Button>
                   <div className="text-gray-700 text-sm p-2 min-h-[36px] font-bold">{copySuccess}</div>
                </div>
            </div>
            <SvgIcon
                className="absolute top-0 right-0 z-0"
                width="1366"
                height="768"
                viewBox="0 0 1366 768"
            >
                <BlueSquiggle />
            </SvgIcon>
        </div>
    )
}

export default SearchRoute;
